import {applyInteractionId, mark, types as metrics, getInteractionId} from '@servicenow/ui-metrics';
import getSpanOptions from './getSpanOptions';
import * as uiAnalyticsConsumer from '@servicenow/ui-analytics-consumer';
const {trace} = uiAnalyticsConsumer;
import {
	errorTypes,
	locations,
	NOW_ID_PROP,
	SPAN_FOR_COMPONENT_ERROR_THROWN,
	SPAN_KIND_CLIENT,
	SPAN_FOR_DISPATCH,
	SPAN_ATTR_DISPATCH_ACTION_TYPE,
	SPAN_ATTR_DISPATCH_MESSAGE
} from './constants';
import {allLogTypes as logTypes} from './logHandler';
import {findKey} from '@servicenow/ui-utils';
import {INTERNAL_HOST_DISPATCH} from './symbols';
const notifiedHosts = new Set();

/**
 * Wrap the execution of code with a try catch and dispatch the error
 * @function sandbox
 * @private
 * @example
 * import {sandbox, errorTypes, locations} from '@servicenow/ui-internal'
 *
 * sandbox(
 *   onChange,
 *   {
 *     args: [value, previousValue, dispatch],
 *     dispatch,
 *     host,
 *     location: locations.LIFECYCLE,
 *     details: {name: 'onChange', propertyName},
 *     errorType: errorTypes.COMPONENT_ERROR_THROWN
 *   }
 * )
 *
 * @param {Function} fn - arbitrary function to execute
 * @param {Object} options - The sandbox options
 * @param {Array} [options.args] - Arguments to pass into the fn.
 * @param {Object} [options.dispatch] - Dispatch function, required.
 * @param {string} [options.location] - Enum of acceptable locations, import {locations} from '@servicenow/ui-internal', required.
 * @param {Object} [options.host] - The host most closely related to the executing code.
 * @param {Object} [options.details] - Object to store arbitrary data
 * @param {string} [options.errorType] - Type of action to dispatch on error caught. Default is COMPONENT_ERROR_THROWN
 * @param {Object} [options.metadata] - Metadata to be passed to the dispatch function
 * @param {function} [options.log] - Logger to log the error
 * @returns {any} Value returned from the function
 */
function sandbox(fn, options = {}) {
	const {args = []} = options;
	try {
		const value = fn(...args);
		return value;
	} catch (error) {
		notify(error, options);
	}
}

export function notify(
	error,
	{
		dispatch,
		host,
		location,
		details = {},
		errorType = errorTypes.COMPONENT_ERROR_THROWN,
		metadata = {},
		log
	}
) {
	const tagName = host.tagName && host.tagName.toLowerCase();
	const componentId = host[NOW_ID_PROP];
	const errorActionType = findKey(errorTypes, val => val === errorType);
	applyInteractionId(metadata);
	const interactionId = getInteractionId(metadata);
	mark(host, interactionId, metrics.ERROR, {location});

	// This if statement prevents a recursive loop when an error is thrown within the COMPONENT_ERROR_THROWN effect
	if (!isRecursive({location, details})) {
		const lifecycleSpan =
			errorType === errorTypes.COMPONENT_ERROR_THROWN
				? trace(SPAN_FOR_DISPATCH, {
						...getSpanOptions(host),
						traceId: metadata.span && metadata.span.traceId,
						kind: SPAN_KIND_CLIENT,
						parentSpan: metadata.span
				  })
				: null;

		if (lifecycleSpan) {
			lifecycleSpan.setAttribute(SPAN_ATTR_DISPATCH_ACTION_TYPE, SPAN_FOR_COMPONENT_ERROR_THROWN);
			lifecycleSpan.setAttribute(SPAN_ATTR_DISPATCH_MESSAGE, error.message);
			metadata.span = lifecycleSpan;
			metadata[INTERNAL_HOST_DISPATCH] = true;
		}

		const hostIdentifier = [
			tagName,
			componentId,
			errorType,
			location,
			Object.keys(details).length > 10
				? Object.keys(details).length
				: Object.entries(details).flat()
		]
			.filter(Boolean)
			.join('-');
		if (!notifiedHosts.has(hostIdentifier)) {
			notifiedHosts.add(hostIdentifier);
			dispatch(errorType, {host, location, details, error}, {internal: true, ...metadata}, true);
			setTimeout(() => notifiedHosts.delete(hostIdentifier), 0); // Clean notifiedHosts after the next tick
		} else if (lifecycleSpan) {
			lifecycleSpan.setAttribute('error.type', 'recursive');
		}

		lifecycleSpan && lifecycleSpan.end();
	}

	if (log) {
		log(
			`An Error occured in <${tagName} component-id="${componentId}"/>. ${errorActionType} action type is dispatched with error details.`,
			{error, level: logTypes.ERROR, host, origin: 'sandbox'}
		);
	}
}

// Note: Ideally we would want to remove this as we already have more generic check
// using hostIdentifier & notifiedHosts to prevent recursive calls.
//TODO: Revisit this function and remove if not needed
function isRecursive({location, details}) {
	if (location === locations.EFFECT && details.actionType === errorTypes.COMPONENT_ERROR_THROWN)
		return true;

	return false;
}

export default sandbox;
